/**
 * AccountHealthScore — Visual health score with dimension breakdown.
 *
 * Displays:
 * - Overall health score (0-100) with color-coded gauge
 * - Trend indicator (improving/stable/declining)
 * - Four dimension scores (relationship, intelligence, momentum, risk)
 * - Detected gaps with severity
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Activity, ChevronDown, ChevronRight, Loader2, AlertCircle,
  TrendingUp, TrendingDown, Minus, RefreshCw, Users, Brain,
  Zap, Shield, AlertTriangle,
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import './AccountHealthScore.css';

const DIMENSION_CONFIG = {
  relationship_health: {
    icon: Users,
    label: 'Relationship',
    description: 'Contact depth and engagement',
  },
  intelligence_health: {
    icon: Brain,
    label: 'Intelligence',
    description: 'Knowledge completeness',
  },
  momentum_health: {
    icon: Zap,
    label: 'Momentum',
    description: 'Recent activity and pipeline',
  },
  risk_health: {
    icon: Shield,
    label: 'Risk',
    description: 'Threats and blockers',
  },
};

function getScoreColor(score) {
  if (score >= 80) return 'var(--color-success)';
  if (score >= 60) return 'var(--color-primary)';
  if (score >= 40) return 'var(--color-warning)';
  return 'var(--color-error)';
}

function getScoreLabel(score) {
  if (score >= 80) return 'Excellent';
  if (score >= 60) return 'Good';
  if (score >= 40) return 'Fair';
  if (score >= 20) return 'Poor';
  return 'Critical';
}

function AccountHealthScore({ accountId, collapsed = false }) {
  const queryClient = useQueryClient();
  const [isExpanded, setIsExpanded] = useState(!collapsed);
  const [showGaps, setShowGaps] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['account-intelligence', 'health', accountId],
    queryFn: () => apiClient.get(`/api/account-intelligence/accounts/${accountId}/health`),
    enabled: !!accountId,
    staleTime: 120_000, // 2 minutes
  });

  const refreshMutation = useMutation({
    mutationFn: () =>
      apiClient.post(`/api/account-intelligence/accounts/${accountId}/health/refresh`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-intelligence', 'health', accountId] });
    },
  });

  const { data: gapsData } = useQuery({
    queryKey: ['account-intelligence', 'gaps', accountId],
    queryFn: () => apiClient.get(`/api/account-intelligence/accounts/${accountId}/gaps`),
    enabled: !!accountId && showGaps,
    staleTime: 120_000,
  });

  const health = data?.health || {};
  const gaps = gapsData?.gaps || {};
  const overallScore = health.overall_health ?? null;
  const trend = health.health_trend ?? 0;
  const trendLabel = health.trend_label || 'stable';

  const allGaps = [
    ...(gaps.contact_gaps || []),
    ...(gaps.intelligence_gaps || []),
    ...(gaps.engagement_gaps || []),
  ];

  if (!accountId) return null;

  return (
    <div className="ahs-container">
      <button
        className="ahs-header"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <div className="ahs-header-left">
          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <Activity size={16} />
          <span className="ahs-title">Account Health</span>
          {overallScore !== null && (
            <span
              className="ahs-score-badge"
              style={{ background: getScoreColor(overallScore) }}
            >
              {overallScore}
            </span>
          )}
        </div>
        {!isExpanded && overallScore !== null && (
          <span className="ahs-preview">{getScoreLabel(overallScore)}</span>
        )}
      </button>

      {isExpanded && (
        <div className="ahs-body">
          {isLoading ? (
            <div className="ahs-loading">
              <Loader2 size={16} className="ahs-spinner" />
              <span>Computing health score...</span>
            </div>
          ) : error ? (
            <div className="ahs-error">
              <AlertCircle size={14} />
              <span>Failed to load health score</span>
            </div>
          ) : overallScore === null ? (
            <div className="ahs-empty">
              <Activity size={20} />
              <p>No health data yet</p>
              <button
                className="ahs-compute-btn"
                onClick={() => refreshMutation.mutate()}
                disabled={refreshMutation.isPending}
              >
                {refreshMutation.isPending ? (
                  <Loader2 size={14} className="ahs-spinner" />
                ) : (
                  <RefreshCw size={14} />
                )}
                Compute Health Score
              </button>
            </div>
          ) : (
            <>
              {/* Main Score */}
              <div className="ahs-main-score">
                <div className="ahs-gauge">
                  <svg viewBox="0 0 100 50" className="ahs-gauge-svg">
                    {/* Background arc */}
                    <path
                      d="M 10 50 A 40 40 0 0 1 90 50"
                      fill="none"
                      stroke="var(--color-border)"
                      strokeWidth="8"
                      strokeLinecap="round"
                    />
                    {/* Score arc */}
                    <path
                      d="M 10 50 A 40 40 0 0 1 90 50"
                      fill="none"
                      stroke={getScoreColor(overallScore)}
                      strokeWidth="8"
                      strokeLinecap="round"
                      strokeDasharray={`${(overallScore / 100) * 126} 126`}
                    />
                  </svg>
                  <div className="ahs-gauge-value">
                    <span className="ahs-gauge-number">{overallScore}</span>
                    <span className="ahs-gauge-label">{getScoreLabel(overallScore)}</span>
                  </div>
                </div>

                <div className="ahs-trend">
                  {trendLabel === 'improving' ? (
                    <TrendingUp size={14} style={{ color: 'var(--color-success)' }} />
                  ) : trendLabel === 'declining' ? (
                    <TrendingDown size={14} style={{ color: 'var(--color-error)' }} />
                  ) : (
                    <Minus size={14} style={{ color: 'var(--color-text-muted)' }} />
                  )}
                  <span>
                    {trend > 0 ? '+' : ''}
                    {trend} from last
                  </span>
                </div>

                <button
                  className="ahs-refresh-btn"
                  onClick={() => refreshMutation.mutate()}
                  disabled={refreshMutation.isPending}
                  title="Refresh health score"
                >
                  <RefreshCw
                    size={14}
                    className={refreshMutation.isPending ? 'ahs-spinner' : ''}
                  />
                </button>
              </div>

              {/* Dimension Scores */}
              <div className="ahs-dimensions">
                {Object.entries(DIMENSION_CONFIG).map(([key, config]) => {
                  const score = health[key];
                  const Icon = config.icon;
                  if (score === undefined || score === null) return null;

                  return (
                    <div key={key} className="ahs-dimension">
                      <div className="ahs-dim-header">
                        <Icon size={12} />
                        <span className="ahs-dim-label">{config.label}</span>
                        <span
                          className="ahs-dim-score"
                          style={{ color: getScoreColor(score) }}
                        >
                          {score}
                        </span>
                      </div>
                      <div className="ahs-dim-bar">
                        <div
                          className="ahs-dim-fill"
                          style={{
                            width: `${score}%`,
                            background: getScoreColor(score),
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Gaps Toggle */}
              <button
                className="ahs-gaps-toggle"
                onClick={() => setShowGaps(!showGaps)}
              >
                <AlertTriangle size={12} />
                {showGaps ? 'Hide Gaps' : 'Show Detected Gaps'}
                {allGaps.length > 0 && !showGaps && (
                  <span className="ahs-gaps-count">{allGaps.length}</span>
                )}
              </button>

              {showGaps && allGaps.length > 0 && (
                <div className="ahs-gaps-list">
                  {allGaps.map((gap, i) => (
                    <div
                      key={i}
                      className={`ahs-gap ${gap.severity === 'critical' ? 'ahs-gap-critical' : gap.severity === 'high' ? 'ahs-gap-high' : ''}`}
                    >
                      <span className="ahs-gap-type">{gap.type}</span>
                      <span className="ahs-gap-desc">{gap.description}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default AccountHealthScore;
